const DATABASE = 'studioaeye-choreo-collaboration-v1';
const VERSION = 1;
const OPERATIONS = 'operations';
const KEYS = 'keys';
export const MAX_OFFLINE_OPERATIONS = 2000;
export const MAX_OFFLINE_BYTES = 5 * 1024 * 1024;
export const OFFLINE_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

const memory = new Map();
let indexedDbFallback = false;
// #17 requirement 5(c): distinct from `indexedDbFallback` (which is also true
// for a browser that never had IndexedDB at all, e.g. private mode) — this
// stamps the moment a database that WAS opening/working started failing or
// failed post-delete verification. Only that condition is a fresh INCIDENT
// the recovery panel should call out; a browser with no IndexedDB support at
// all is already covered by the existing "cannot be stored durably" warning.
let storageIncidentAt = 0;
const encode = (value) => new TextEncoder().encode(value);
const decode = (value) => new TextDecoder().decode(value);
export const queueScopeKey = ({ userId, organisationId, routineId, draftId, clientInstanceId }) =>
  [userId, organisationId, routineId, draftId, clientInstanceId].map(String).join(':');

// #17 requirement 5(c): read by the collaboration hook to select the
// "storage is failing" recovery copy instead of the generic UNSENDABLE copy.
export function collaborationStorageIncident() { return indexedDbFallback && storageIncidentAt > 0; }

function openOnce() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(OPERATIONS)) database.createObjectStore(OPERATIONS, { keyPath: 'id' });
      if (!database.objectStoreNames.contains(KEYS)) database.createObjectStore(KEYS, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function deleteDatabaseOnce() {
  return new Promise((resolve) => {
    const request = indexedDB.deleteDatabase(DATABASE);
    request.onsuccess = () => resolve(true);
    request.onerror = () => resolve(false);
    request.onblocked = () => resolve(false);
  });
}

// #17 requirement 5(a): a corrupted per-origin database (a bad schema state,
// a disk-quota eviction mid-write, a browser storage bug) used to have exactly
// one outcome — a silent, PERMANENT fall back to in-memory storage (origin-
// scoped, single tab, lost on reload), which is what the owner hit as a
// transient "cannot be stored durably" warning that never actually cleared on
// reload. A suspected-corrupt open now gets exactly ONE recovery attempt:
// delete the database outright and reopen, before falling back to memory. A
// second failure — against the fresh, just-deleted database — means IndexedDB
// is genuinely unavailable (private browsing, disabled storage), not
// corruption, and falls back exactly as before.
async function openDatabase() {
  if (!globalThis.indexedDB) return null;
  try { return await openOnce(); } catch {
    storageIncidentAt = Date.now();
    const deleted = await deleteDatabaseOnce();
    if (deleted) { try { return await openOnce(); } catch { /* fall through to memory */ } }
    indexedDbFallback = true; return null;
  }
}

const requestValue = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

async function cryptoKey(database, userId) {
  if (!database || !globalThis.crypto?.subtle) return null;
  const id = `user:${userId}`;
  // An IndexedDB transaction auto-commits once control returns to the event
  // loop, so it must never be held across the WebCrypto awaits: Firefox and
  // WebKit otherwise fail every queue write with an inactive-transaction error.
  const current = await requestValue(database.transaction(KEYS, 'readonly').objectStore(KEYS).get(id));
  if (current?.key) return current.key;
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  await requestValue(database.transaction(KEYS, 'readwrite').objectStore(KEYS).put({ id, key, createdAt: Date.now() }));
  return key;
}

async function seal(database, userId, value) {
  const plain = encode(JSON.stringify(value));
  const key = await cryptoKey(database, userId);
  if (!key) return { protected: false, plain: JSON.stringify(value), size: plain.byteLength };
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain);
  return { protected: true, iv, cipher, size: plain.byteLength };
}

async function unseal(database, userId, record) {
  if (!record.protected) return JSON.parse(record.plain);
  const key = await cryptoKey(database, userId);
  if (!key) throw new Error('Offline recovery key is unavailable.');
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: record.iv }, key, record.cipher);
  return JSON.parse(decode(plain));
}

async function records(database) {
  if (!database) return [...memory.values()];
  return requestValue(database.transaction(OPERATIONS, 'readonly').objectStore(OPERATIONS).getAll());
}

async function removeRecords(database, ids) {
  if (!database) { ids.forEach((id) => memory.delete(id)); return; }
  const transaction = database.transaction(OPERATIONS, 'readwrite');
  ids.forEach((id) => transaction.objectStore(OPERATIONS).delete(id));
  await new Promise((resolve, reject) => { transaction.oncomplete = resolve; transaction.onerror = () => reject(transaction.error); });
}

// #17 requirement 5(b): removeCollaborationQueueItem and discardCollaborationQueue
// are the two paths a person's explicit recovery choice depends on — "Just show
// me the latest version", or a successful merge draining its durable entry. A
// delete that reports success but does not actually stick means the SAME
// poisoned item comes back on the next load and no recovery action ever
// converges (the live incident this fixes: the warning flashed, but reloading
// kept finding the same stuck queue). Re-read after delete before trusting it;
// on a mismatch, delete-and-recreate the WHOLE per-origin database — IndexedDB
// has no per-item repair primitive — and retry once against the fresh, empty
// one, where the target ids trivially clear. A browser this flaky loses its
// OTHER scopes' queued recovery text too: an accepted, rare cost of actually
// getting unwedged, and this never silently reports success without it.
async function removeRecordsVerified(database, ids) {
  if (!ids.length) return database;
  if (!database) { ids.forEach((id) => memory.delete(id)); return database; }
  const attempt = async (db) => {
    await removeRecords(db, ids);
    const remaining = await Promise.all(ids.map((id) => requestValue(db.transaction(OPERATIONS, 'readonly').objectStore(OPERATIONS).get(id))));
    if (remaining.some(Boolean)) throw new Error('Offline recovery storage did not clear as reported.');
  };
  try { await attempt(database); return database; } catch {
    storageIncidentAt = Date.now();
    const deleted = await deleteDatabaseOnce();
    if (deleted) {
      try { const fresh = await openOnce(); await attempt(fresh); return fresh; } catch { /* fall through to memory */ }
    }
    indexedDbFallback = true; ids.forEach((id) => memory.delete(id)); return null;
  }
}

export async function loadCollaborationQueue(scope, now = Date.now()) {
  const database = await openDatabase();
  const scopeKey = queueScopeKey(scope);
  const all = await records(database);
  const expired = all.filter((record) => !Number.isFinite(Number(record.createdAt)) || Number(record.createdAt) <= 0
    || now - Number(record.createdAt) >= OFFLINE_RETENTION_MS).map((record) => record.id);
  if (expired.length) await removeRecords(database, expired);
  const selected = all.filter((record) => record.scopeKey === scopeKey && !expired.includes(record.id))
    .sort((left, right) => left.clientSequence - right.clientSequence);
  return Promise.all(selected.map((record) => unseal(database, scope.userId, record)));
}

export async function collaborationQueueProtection(scope) {
  const database = await openDatabase();
  // WebCrypto (encryption) capability is independent of IndexedDB (persistence). Report it
  // separately so the UI never misattributes a missing persistent store to an inability to
  // encrypt: D004 §8 requires the "cannot encrypt" statement to reflect the actual threat
  // model, not merely the absence of durable storage.
  const capable = Boolean(globalThis.crypto?.subtle);
  if (!database || indexedDbFallback) return { persistent: false, capable, encrypted: false, protected: false };
  try {
    const key = await cryptoKey(database, scope.userId);
    return { persistent: true, capable, encrypted: Boolean(key), protected: Boolean(key) };
  } catch {
    return { persistent: true, capable, encrypted: false, protected: false };
  }
}

export async function putCollaborationQueueItem(scope, item) {
  const database = await openDatabase();
  const scopeKey = queueScopeKey(scope);
  const id = `${scopeKey}:${item.operationId}`;
  const current = (await records(database)).filter((record) => record.scopeKey === scopeKey && record.id !== id);
  const normalized = { ...item, createdAt: Number.isFinite(Number(item.createdAt)) && Number(item.createdAt) > 0 ? Number(item.createdAt) : Date.now() };
  const sealed = await seal(database, scope.userId, normalized);
  if (current.length >= MAX_OFFLINE_OPERATIONS || current.reduce((total, record) => total + Number(record.size || 0), 0) + sealed.size > MAX_OFFLINE_BYTES) {
    const error = new Error('Offline recovery storage is full. Export your text or discard queued changes.');
    error.code = 'OFFLINE_QUEUE_FULL'; throw error;
  }
  const record = { id, scopeKey, userId: String(scope.userId),
    clientSequence: normalized.clientSequence, createdAt: normalized.createdAt, ...sealed };
  if (!database) { memory.set(record.id, record); return; }
  await requestValue(database.transaction(OPERATIONS, 'readwrite').objectStore(OPERATIONS).put(record));
}

export async function removeCollaborationQueueItem(scope, operationId) {
  const database = await openDatabase();
  await removeRecordsVerified(database, [`${queueScopeKey(scope)}:${operationId}`]);
}

export async function updateCollaborationQueueItem(scope, item) {
  const database = await openDatabase();
  const scopeKey = queueScopeKey(scope);
  const id = `${scopeKey}:${item.operationId}`;
  const normalized = { ...item, createdAt: Number.isFinite(Number(item.createdAt)) && Number(item.createdAt) > 0 ? Number(item.createdAt) : Date.now() };
  const sealed = await seal(database, scope.userId, normalized);
  const current = (await records(database)).filter((record) => record.scopeKey === scopeKey && record.id !== id);
  if (current.length >= MAX_OFFLINE_OPERATIONS || current.reduce((total, record) => total + Number(record.size || 0), 0) + sealed.size > MAX_OFFLINE_BYTES) {
    const error = new Error('Offline recovery storage is full. Export your text or discard queued changes.');
    error.code = 'OFFLINE_QUEUE_FULL'; throw error;
  }
  const record = { id, scopeKey, userId: String(scope.userId),
    clientSequence: normalized.clientSequence, createdAt: normalized.createdAt, ...sealed };
  if (!database) { memory.set(record.id, record); return; }
  await requestValue(database.transaction(OPERATIONS, 'readwrite').objectStore(OPERATIONS).put(record));
}

export async function discardCollaborationQueue(scope) {
  const database = await openDatabase();
  const scopeKey = queueScopeKey(scope);
  await removeRecordsVerified(database, (await records(database)).filter((record) => record.scopeKey === scopeKey).map((record) => record.id));
}

export async function purgeCollaborationQueues(userId) {
  if (userId === undefined || userId === null) return;
  const database = await openDatabase();
  await removeRecords(database, (await records(database)).filter((record) => record.userId === String(userId)).map((record) => record.id));
  if (database) await requestValue(database.transaction(KEYS, 'readwrite').objectStore(KEYS).delete(`user:${userId}`));
}
